import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Badge from "../components/ui/Badge";
import IconBadge from "../components/ui/IconBadge";
import Icon from "../components/ui/Icon";
import GraficoSaldo from "../components/ui/GraficoSaldo";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import { baixarCsv } from "../utils/exportar";
import { formatarHoras, horaLocal } from "../utils/tempo";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const STATUS = {
  falta: { label: "Falta", cor: "#DC2626", bg: "#FEE2E2" },
  completo: { label: "Completo", cor: "#16A34A", bg: "#DCFCE7" },
  completo_banco: { label: "Completo (banco de horas)", cor: "#7C3AED", bg: "#EDE9FE" },
  hora_a_menos: { label: "Horas a menos", cor: "#D97706", bg: "#FEF3C7" },
  batida_incompleta: { label: "Batida incompleta", cor: "#D97706", bg: "#FEF3C7" },
  hora_extra: { label: "Hora extra", cor: "#2563EB", bg: "#DBEAFE" },
  hora_extra_pendente: { label: "Hora extra (aguardando aprovação)", cor: "#D97706", bg: "#FEF3C7" },
  hora_extra_rejeitada: { label: "Hora extra não aprovada", cor: "#6B7280", bg: "#F3F4F6" },
  trabalhado: { label: "Trabalhado", cor: "#16A34A", bg: "#DCFCE7" },
  folga: { label: "Folga", cor: "#6B7280", bg: "#F3F4F6" },
  trabalhou_folga: { label: "Trabalhou na folga", cor: "#7C3AED", bg: "#EDE9FE" },
  feriado: { label: "Feriado", cor: "#0891B2", bg: "#CFFAFE" },
  trabalhou_feriado: { label: "Trabalhou no feriado", cor: "#7C3AED", bg: "#EDE9FE" },
  ferias: { label: "Férias", cor: "#0891B2", bg: "#CFFAFE" },
  atestado: { label: "Atestado médico", cor: "#0891B2", bg: "#CFFAFE" },
  outro: { label: "Afastamento", cor: "#0891B2", bg: "#CFFAFE" },
  atestado_pendente: { label: "Atestado (aguardando aprovação)", cor: "#D97706", bg: "#FEF3C7" },
  sem_registro: { label: "Sem escala definida", cor: "#9CA3AF", bg: "#F9FAFB" },
  em_andamento: { label: "Em andamento", cor: COLORS.primary, bg: COLORS.primaryBg },
  pendente_aprovacao: { label: "Aguardando aprovação", cor: "#D97706", bg: "#FEF3C7" },
  trabalho_previsto: { label: "Trabalho previsto", cor: "#2563EB", bg: "#DBEAFE" },
  futuro: { label: "—", cor: "#D1D5DB", bg: "#FFFFFF" },
  antes_cadastro: { label: "Antes do cadastro", cor: "#D1D5DB", bg: "#FFFFFF" },
};

const TIPO_AFASTAMENTO_LABEL = { ferias: "Férias", atestado: "Atestado médico", outro: "Afastamento" };
const AFASTAMENTO_STATUS_TONE = { pendente: "warning", aprovado: "success", rejeitado: "danger" };
const AFASTAMENTO_STATUS_LABEL = { pendente: "Aguardando aprovação", aprovado: "Aprovado", rejeitado: "Recusado" };


function horaDoTipoTexto(dia, tipo) {
  const r = dia.registros.find((x) => x.tipo === tipo);
  return r ? horaLocal(r.horario) : "—";
}

// Cada horário batido linka pro comprovante daquela batida específica (IP,
// localização, dispositivo — respaldo jurídico sob demanda, ver Comprovante.js).
function horaDoTipo(dia, tipo) {
  const r = dia.registros.find((x) => x.tipo === tipo);
  if (!r) return "—";
  return (
    <Link
      to={`/comprovante/${r.id}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, color: COLORS.text, textDecoration: "none" }}
      title="Ver comprovante dessa batida"
    >
      {horaLocal(r.horario)}
      {r.origem === "manual" && (
        <span title="Lançada manualmente, não foi uma batida pelo app" style={{ display: "inline-flex", color: COLORS.textLight }}>
          <Icon name="edit" size={11} />
        </span>
      )}
      {r.origem === "auto" && (
        <span title="Fechamento automático — a pessoa não bateu, o sistema fechou sozinho 30min depois do horário" style={{ display: "inline-flex", color: COLORS.warning }}>
          <Icon name="alertCircle" size={11} />
        </span>
      )}
    </Link>
  );
}

export default function MeuHistorico() {
  const { token } = useAuth();
  const [ref, setRef] = useState(() => { const d = new Date(); return { ano: d.getFullYear(), mes: d.getMonth() + 1 }; });
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  // Autoatendimento do funcionário (enviar o próprio atestado) foi removido
  // — pedido do usuário 2026-10-25: por enquanto só o gestor anexa atestado
  // (ver AtrasosFaltas.js), direto já aprovado. Essa lista continua existindo
  // só pra mostrar pro funcionário os afastamentos que o GESTOR lançou pra
  // ele (GET /api/afastamentos/meus retorna isso independente de quem criou).
  const [meusAtestados, setMeusAtestados] = useState([]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const data = await apiFetch("GET", `/api/ponto/meu-historico?ano=${ref.ano}&mes=${ref.mes}`, undefined, token);
      setDados(data);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [ref, token]);

  useEffect(() => { carregar(); }, [carregar]);

  const exportarCsv = () => {
    if (!dados) return;
    const cabecalhos = ["Dia", "Entrada", "Intervalo início", "Intervalo fim", "Saída", "Horas trabalhadas", "Banco de horas", "Saldo do dia", "Saldo acumulado no mês", "Status"];
    const linhas = dados.dias
      .filter((dia) => dia.status !== "futuro")
      .map((dia) => [
        `${DIAS_SEMANA[dia.dia_semana]} ${dia.data.split("-").reverse().join("/")}`,
        horaDoTipoTexto(dia, "entrada"),
        horaDoTipoTexto(dia, "intervalo_inicio"),
        horaDoTipoTexto(dia, "intervalo_fim"),
        horaDoTipoTexto(dia, "saida"),
        dia.registros.length > 0 ? formatarHoras(dia.horas_trabalhadas) : "",
        dia.horas_banco > 0 ? formatarHoras(dia.horas_banco) : "",
        dia.saldo_dia === null ? "" : formatarHoras(dia.saldo_dia),
        dia.saldo_acumulado === null ? "" : formatarHoras(dia.saldo_acumulado),
        STATUS[dia.status]?.label || "",
      ]);
    baixarCsv(`meu-historico-${String(ref.mes).padStart(2, "0")}-${ref.ano}.csv`, cabecalhos, linhas);
  };

  const mudarMes = (delta) => {
    let { ano, mes } = ref;
    mes += delta;
    if (mes > 12) { mes = 1; ano += 1; }
    if (mes < 1) { mes = 12; ano -= 1; }
    setRef({ ano, mes });
  };

  const carregarMeusAtestados = useCallback(async () => {
    const data = await apiFetch("GET", "/api/afastamentos/meus", undefined, token);
    setMeusAtestados(data);
  }, [token]);

  useEffect(() => { carregarMeusAtestados(); }, [carregarMeusAtestados]);

  // Último dia do mês que já tem saldo acumulado calculado (dias futuros ficam
  // null) — é o "saldo do mês até agora", mostrado separado da tabela dia a dia
  // pra não parecer resultado de um dia só.
  const saldoMensal = (() => {
    const dias = dados?.dias || [];
    for (let i = dias.length - 1; i >= 0; i--) {
      if (dias[i].saldo_acumulado !== null) return dias[i].saldo_acumulado;
    }
    return null;
  })();

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 14 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Meu Histórico
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Suas batidas, faltas e horas dia a dia.
          </p>
        </div>

        <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: COLORS.surface, border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "6px 10px" }}>
            <button onClick={() => mudarMes(-1)} style={navBtn()}>‹</button>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, minWidth: 130, textAlign: "center" }}>
              {MESES[ref.mes - 1]} {ref.ano}
            </span>
            <button onClick={() => mudarMes(1)} style={navBtn()}>›</button>
          </div>
          <Btn variant="secondary" onClick={exportarCsv} disabled={!dados}>Baixar CSV</Btn>
          <Btn variant="secondary" onClick={() => window.print()} disabled={!dados}>Imprimir / PDF</Btn>
        </div>
      </div>

      {dados && (
        <Card className="no-print" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <IconBadge icon="barChart" size={30} cor={COLORS.primary} />
              <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", margin: 0 }}>
                Saldo do banco de horas no mês
              </p>
            </div>
            {saldoMensal !== null && (
              <div style={{ textAlign: "right" }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 1px" }}>
                  Acumulado no mês
                </p>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0, fontVariantNumeric: "tabular-nums", color: saldoMensal > 0 ? COLORS.success : saldoMensal < 0 ? COLORS.danger : COLORS.textMid }}>
                  {formatarHoras(saldoMensal)}
                </p>
              </div>
            )}
          </div>
          <GraficoSaldo dias={dados.dias} />
        </Card>
      )}

      {meusAtestados.length > 0 && (
        <Card className="no-print" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <IconBadge icon="heart" size={30} cor={COLORS.primary} />
            <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", margin: 0 }}>
              Meus afastamentos
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {meusAtestados.map((a) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "8px 10px", background: COLORS.bg, borderRadius: RADIUS.sm }}>
                <div style={{ fontSize: 13, color: COLORS.text }}>
                  <strong>{TIPO_AFASTAMENTO_LABEL[a.tipo] || a.tipo}</strong>{" "}
                  {a.data_inicio.split("-").reverse().join("/")} a {a.data_fim.split("-").reverse().join("/")}
                  {a.status === "rejeitado" && a.motivo_rejeicao && (
                    <span style={{ color: COLORS.textMid }}> — "{a.motivo_rejeicao}"</span>
                  )}
                </div>
                <Badge tone={AFASTAMENTO_STATUS_TONE[a.status]}>{AFASTAMENTO_STATUS_LABEL[a.status]}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="tabela-mes-print" style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : erro ? (
          <div style={{ padding: 24, fontSize: 13.5, color: COLORS.danger }}>{erro}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  {["Dia", "Entrada", "Intervalo", "Saída", "Horas", "Banco de horas", "Saldo do dia", "Status"].map((h) => (
                    <th key={h} className={h === "" ? "no-print" : undefined} style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dados.dias.map((dia) => {
                  const futuro = dia.eh_futuro;
                  // Em dias futuros a escala já é conhecida (segunda-sexta/segunda-sábado
                  // dão pra prever), mesmo sem saber ainda se a pessoa vai bater ponto —
                  // 12x36 continua sem previsão por falta de data de referência no cadastro.
                  // Se o dia futuro já tem um afastamento aprovado/pendente cobrindo ele,
                  // `status` deixa de ser "futuro" (ver pontoCalculo.js) e cai direto no
                  // branch de baixo, mostrando o status real em vez do genérico.
                  const cfg = dia.status === "futuro"
                    ? (dia.esperado === true ? STATUS.trabalho_previsto : dia.esperado === false ? STATUS.folga : null)
                    : STATUS[dia.status] || STATUS.sem_registro;
                  const temIntervalo = dia.registros.some((r) => r.tipo === "intervalo_inicio" || r.tipo === "intervalo_fim");
                  return (
                    <tr key={dia.data} style={{ borderBottom: `1px solid ${COLORS.border}`, opacity: futuro || dia.status === "antes_cadastro" ? 0.65 : 1 }}>
                      <td style={{ padding: "10px 16px", fontSize: 13, whiteSpace: "nowrap" }}>
                        <span style={{ fontWeight: 700, color: COLORS.text }}>{DIAS_SEMANA[dia.dia_semana]}</span>{" "}
                        <span style={{ color: COLORS.textMid }}>{dia.data.slice(-2)}/{dia.data.slice(5, 7)}</span>
                      </td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text }}>{horaDoTipo(dia, "entrada")}</td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text }}>
                        {temIntervalo ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {horaDoTipo(dia, "intervalo_inicio")} – {horaDoTipo(dia, "intervalo_fim")}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text }}>{horaDoTipo(dia, "saida")}</td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
                        {dia.registros.length > 0 ? formatarHoras(dia.horas_trabalhadas) : "—"}
                      </td>
                      <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
                        {dia.horas_banco > 0 ? formatarHoras(dia.horas_banco) : "—"}
                      </td>
                      <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                        {dia.saldo_dia === null ? (
                          <span style={{ color: COLORS.textLight, fontWeight: 400 }}>—</span>
                        ) : (
                          <span style={{ color: dia.saldo_dia > 0 ? COLORS.success : dia.saldo_dia < 0 ? COLORS.danger : COLORS.textMid }}>
                            {formatarHoras(dia.saldo_dia)}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "10px 16px" }}>
                        {cfg && (
                          <span
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 6, background: cfg.bg, color: cfg.cor,
                              borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                            }}
                          >
                            {cfg.label}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

    </div>
  );
}

function navBtn() {
  return { background: "transparent", border: "none", fontSize: 18, color: COLORS.textMid, cursor: "pointer", padding: "0 4px", lineHeight: 1 };
}

